import { GenericMapService } from '@common-modules/wlm-map/generic-map/services/generic-map.service';
// prettier-ignore
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CtMetadata } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-metadata';
import { CTMapFiltrableProperties } from '@cloud-twin-v2/ct-map/models/ct-map-filtrable-properties';
import { CTMapPersistencyService } from '@cloud-twin-v2/ct-map/services/ct-map-persistency.service';
import { CTSyncMapGridService } from '@cloud-twin-v2/ct-map/services/ct-sync-map-grid.service';
import {
  FrontendSettingsMapDto,
  LayerThematicStylesDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { DateRange } from '@common-modules/shared/model/date/date-range';
import { WlmResizeObserverService } from '@common-modules/shared/services/resize-observer.service';
import { LayerEventsService } from '@common-modules/wlm-map/components/layers-selector/services/layer-events.service';
import { MapTimeBarComponent } from '@common-modules/wlm-map/components/map-time-bar/map-time-bar.component';
import {
  MapTopBarButtonsComponent,
  MapTopbarButtonsConfiguration,
} from '@common-modules/wlm-map/components/map-top-bar-buttons/map-top-bar-buttons.component';
import { MapSidePanelSettings } from '@common-modules/wlm-map/components/side-panel/map-side-panel-settings';
import {
  LayerItemStyleChangeData,
  SidePanelComponent,
} from '@common-modules/wlm-map/components/side-panel/side-panel.component';
import { Leaflet } from '@common-modules/wlm-map/leaflet-export';
import { LatLong } from '@common-modules/wlm-map/leaflet-map/leaflet-map.component';
import { LayerInfo, LayerInfoTypeEnum, LayerItem } from '@common-modules/wlm-map/models/layer-item';
import { MapDateRange } from '@common-modules/wlm-map/models/map-date-range';
import { LeafletTimeBarService } from '@common-modules/wlm-map/services/leaflet-time-bar.service';
import { LeafletTopBarService } from '@common-modules/wlm-map/services/leaflet-top-bar.service';
import { asEnumerable } from 'linq-es2015';
import { Observable, ReplaySubject, Subscription, take } from 'rxjs';
import { GenericMapSettings } from '../models/generic-map-settings';
import { LayerItemV2 } from '../models/layer-item';
import { LayerVisibilityChangeEvent } from '../models/layer-visibility-change-event';
import { IMapFiltrableProperties } from '../models/map-filtrable-properties';
import { MapGenericStyle } from '../models/map-generic-style';
import { MapTooltipParams } from '../models/map-tooltip-params';
import { NativeLayer } from '../models/native-layer';
import { GenericMapDataService } from '../services/generic-map-data.service';
import { GenericMapTooltipService } from '../services/generic-map-tooltip.service';
import { MapLogService } from '../services/map-log.service';
import { ThematicMapService } from '../services/thematic-map.service';

@Component({
  selector: 'app-generic-map',
  templateUrl: './generic-map.component.html',
  styleUrl: './generic-map.component.scss',
  standalone: false,
  providers: [LeafletTopBarService, GenericMapTooltipService],
})
export class GenericMapComponent implements OnInit, AfterViewInit {
  private _mapSettings: GenericMapSettings;
  get mapSettings(): GenericMapSettings {
    return this._mapSettings;
  }
  @Input() set mapSettings(value: GenericMapSettings) {
    this._mapSettings = value;
    if (this.mapSettings) {
      this.initMapLayers();
      this.mapDateRange = this.mapSettings.dateRange;
      this.topBarSettings = this.mapSettings.topBarSettings;
      this.sidePanelSettings = this.mapSettings.sidePanelSettings;
    }
  }

  private _namespaces: Partial<CTMapFiltrableProperties>;
  get namespaces(): Partial<CTMapFiltrableProperties> {
    return this._namespaces;
  }
  @Input() set namespaces(value: Partial<CTMapFiltrableProperties>) {
    this._namespaces = value;
    this.onExternalFilterChanged(value);
  }

  private _mapFrontendSettings: FrontendSettingsMapDto;

  get mapFrontendSettings(): FrontendSettingsMapDto {
    return this._mapFrontendSettings;
  }
  @Input()
  set mapFrontendSettings(value: FrontendSettingsMapDto) {
    if (value) {
      this._mapFrontendSettings = value;
      this.timeBarVelocity = value.timeBarVelocity;
    }
  }

  @Output() layersVisibilityChange = new EventEmitter<LayerItemV2[]>();
  @Output() layerWithResultsChange = new EventEmitter<LayerItemV2[]>();
  @Output() timeStepChange = new EventEmitter<Date>();

  private _timeBar: MapTimeBarComponent;
  @ViewChild(MapTimeBarComponent) set setTimeBar(timeBar: MapTimeBarComponent) {
    this._timeBar = timeBar;
    this.addTimeBar();
  }

  private _topBar: MapTopBarButtonsComponent;
  @ViewChild(MapTopBarButtonsComponent) set setTopBar(topBar: MapTopBarButtonsComponent) {
    this._topBar = topBar;
    this.addTopBar();
  }

  private _sidePanel: SidePanelComponent;
  @ViewChild(SidePanelComponent) set setSidePanel(sidePanel: SidePanelComponent) {
    this._sidePanel = sidePanel;
    this.addSidePanel();
  }

  map;
  mapDateRange: MapDateRange;
  topBarSettings: MapTopbarButtonsConfiguration;
  sidePanelSettings: MapSidePanelSettings<any>;
  id: string;
  sidePanelId: string;
  timeBarId: string;
  topBarId: string;
  layerItems: LayerItemV2[];
  layersLatLong: LatLong[];
  selectedStep: Date;
  toggleVisible = false;
  metadata: CtMetadata = {};
  timeBarVelocity: number;

  private _layerItemsChange$ = new ReplaySubject<LayerItemV2[]>(1);
  private _sidePanelSetted = false;
  private _topBarSetted = false;
  private _timeBarSetted = false;
  private _useGeolocatedTooltip = true;
  readonly _initialZoom = 7;
  private readonly _basePane = 'baseMap';

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _resizeObserverService: WlmResizeObserverService =
    inject(WlmResizeObserverService);
  private readonly _leafletTimeBarService: LeafletTimeBarService = inject(LeafletTimeBarService);
  private readonly _leafletTopBarService: LeafletTopBarService = inject(LeafletTopBarService);
  private readonly _genericMapService: GenericMapService = inject(GenericMapService);
  private readonly _genericMapDataService: GenericMapDataService = inject(GenericMapDataService);
  private readonly _layersService: LayerEventsService = inject(LayerEventsService);
  private readonly _ctAttributeService = inject(CtAttributesService);
  private readonly _ctMapPersistencyService = inject(CTMapPersistencyService);
  private readonly _log = inject(MapLogService);
  private readonly _thematicMapService = inject(ThematicMapService);
  private readonly _ctSyncMapGridService = inject(CTSyncMapGridService);
  private readonly _genericMapTooltipService = inject(GenericMapTooltipService);
  private _setResultsToLayersSubs: Subscription;
  private _standaloneTooltip: any; // Holds the current standalone tooltip
  private _standaloneTooltipParams: MapTooltipParams;
  private _standaloneTooltipLatLng: any;

  private _resizeContainerSubs: Subscription;
  private _mapContainer: ElementRef<HTMLElement>;
  @ViewChild('mapContainer') set setMapContainer(mapContainer: ElementRef<any>) {
    if (!mapContainer) {
      return;
    }

    this._mapContainer = mapContainer;
    this._resizeContainerSubs?.unsubscribe();

    this.initMapLayers();

    this._resizeContainerSubs = this._resizeObserverService
      .observe({
        el: mapContainer.nativeElement,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        setTimeout(() => {
          this.map?.invalidateSize();
        }, 400);
      });
  }

  constructor(private _cdr: ChangeDetectorRef) {
    this.id = globalUtilsHelper.generateGuid();
    this.sidePanelId = globalUtilsHelper.generateGuid();
    this.timeBarId = globalUtilsHelper.generateGuid();
    this.topBarId = globalUtilsHelper.generateGuid();
    this._leafletTimeBarService.extends();
    this._leafletTopBarService.extends();
  }

  private resetMap(): void {
    this.layerItems = [];
    this.map?.off();
    this.map?.remove();
    this._topBarSetted = false;
    this._timeBarSetted = false;
    this._sidePanelSetted = false;
  }

  ngOnInit(): void {
    this._ctSyncMapGridService.gridSelectedElements$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((elements) => {
        this.getLocalizationFeatures(elements);
      });
    this._layersService.layerVisibility$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((visibilityChange) => {
        if (visibilityChange) {
          this.onLayerVisibilityChanged(visibilityChange);
        }
      });

    this._ctAttributeService.metadata$.pipe(take(1)).subscribe((metadata) => {
      this.metadata = metadata;
    });

    this._layersService.thematicToDelete$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((layerItem) => {
        this.handleThematicToDelete(layerItem);
      });

    this._layersService.editThematic$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((thematic) => {
        this.handleThematicEdited(thematic);
      });

    this._layersService.newThematic$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((thematic) => {
        this.handleThematicCreated(thematic);
      });
  }

  ngAfterViewInit(): void {
    this._cdr.detectChanges();
  }

  private getLocalizationFeatures(elements: any): void {
    const arr = Array.isArray(elements) ? elements : elements ? [elements] : [];
    const selectedIds = arr
      .map((e) => e?.networkElementId ?? e?.dataItem?.networkElementId ?? e?.dataItem?.id ?? e?.id)
      .filter((v) => v != null)
      .map((v) => String(v));

    this.allLayersLoaded$().subscribe(() => {
      const latLongs = [];
      this.map?.eachLayer((nativeLayer: NativeLayer) => {
        if (nativeLayer?.eachLayer) {
          nativeLayer.eachLayer((innerLayer: any) => {
            if (innerLayer?.feature?.properties) {
              const props = innerLayer.feature.properties;
              const canSetStyle = typeof innerLayer.setStyle === 'function';
              if (selectedIds.includes(props.neId)) {
                if (!innerLayer._originalStyle && innerLayer.options) {
                  innerLayer._originalStyle = { ...innerLayer.options };
                }
                let latlng = null;
                if (canSetStyle) {
                  this.applyMapStyles(innerLayer, { color: 'red', weight: 3 });
                }
                if (innerLayer.getLatLng) {
                  // Only points has LatLong
                  latlng = innerLayer.getLatLng();
                } else if (innerLayer.getBounds && innerLayer.getBounds().getCenter) {
                  // For Polygons y Pipes
                  latlng = innerLayer.getBounds().getCenter();
                }
                if (latlng) {
                  latLongs.push(latlng);
                }
              } else {
                if (innerLayer._originalStyle && canSetStyle) {
                  this.applyMapStyles(innerLayer, innerLayer._originalStyle);
                }
              }
            }
          });
        }
      });
      if (latLongs.length) {
        this.map.fitBounds(latLongs);
      }
    });
  }

  /**
   * Aplica estilos a una capa Leaflet de forma centralizada.
   */
  private applyMapStyles(layer: NativeLayer, styles: any): void {
    this._genericMapService.applyMapStyles(layer, styles);
  }

  initMap(): void {
    this.resetMap();

    this.map = Leaflet.map(this._mapContainer.nativeElement, {
      center: [0, 0],
      zoom: this._initialZoom,
      mapId: this.id,
      waterTimeBarOptions: {
        dataRange: this._mapSettings.dateRange,
        currentStep: this.firstStep,
      },
      zoomControl: false,
      preferCanvas: true,
    });

    this._genericMapService.setMap(this.map);

    this._log.log('Init map: Start bindings');

    this.createBaseLayer();
    this.bindEvents();
    this.addTopBar();
    this.addSidePanel();
    this.addTimeBar();
    this.bindTooltip();
  }

  private panToCenter(): void {
    if (!this.map) {
      return;
    }

    this._log.log('Pan to center: Start');
    const bounds = new Leaflet.latLngBounds();

    this.allLayersLoaded$().subscribe(() => {
      this.map?.eachLayer((nativeLayer: NativeLayer) => {
        const currentBounds = this.getLayerCoordinates(nativeLayer);
        if (currentBounds) {
          bounds.extend(currentBounds);
        }

        // Si nativeLayer tiene innerLayers
        if (nativeLayer?.eachLayer) {
          nativeLayer.eachLayer((innerLayer: NativeLayer) => {
            const currentBounds = this.getLayerCoordinates(innerLayer);
            if (currentBounds) {
              bounds.extend(currentBounds);
            }
          });
        }
      });

      // Fit the map to the calculated bounds
      if (bounds.isValid()) {
        this._log.log('Pan to center: Fitting bounds', bounds);
        this.map.fitBounds(bounds);
      } else {
        this._log.log('Pan to center: Bounds not valid', bounds);
      }
    });
  }

  /**
   * Devuelve las coordenadas de una layer usando getLatLng o el centro de getBounds.
   */
  private getLayerCoordinates(layer: NativeLayer): any {
    if (layer.getLatLng && typeof layer.getLatLng === 'function') {
      return layer.getLatLng();
    }
    if (layer.getBounds && typeof layer.getBounds === 'function') {
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid && bounds.isValid()) {
        return bounds;
      }
    }
    return null;
  }

  private createBaseLayer(): void {
    const baseLayer = this.getBaseMapLayer();
    const basePane = this.map.createPane(baseLayer.pane);
    basePane.style.zIndex = 0;
    baseLayer.nativeLayer.addTo(this.map);
  }

  private initMapLayers(): void {
    if (
      !this._mapContainer?.nativeElement ||
      !this.isMapEmptyElementLoaded(this._mapContainer) ||
      !this.mapSettings
    ) {
      return;
    }

    this.initMap();

    this._genericMapService.buildLayerItems(this._mapSettings.layerItems).subscribe({
      next: (layerItems) => {
        this.layerItems = layerItems;
        this.layerItems = this.getSortedLayerItems(this.layerItems);
        this.addMapLayers(this.layerItems);
        this.onStepChanged(this.firstStep);
        this._layerItemsChange$.next(this.layerItems);
        // this.onLayerItemsOrderChanged(this.layerItems);
        this.sendLayerItemsVisibles(this.layerItems);
        this.panToCenter();
        this._log.log('Init map: Build layers');
        this.bindTooltip();

        setTimeout(() => {
          this._genericMapService.applyStylesToBaseLayers(this.layerItems, this.map);
        });
      },
      error: this.handleError,
    });
  }

  private addMapLayers(layerItems: LayerItemV2[]): void {
    this.buildPanes(layerItems);
    layerItems.forEach((layerItem) => {
      layerItem.nativeLayer.pane = layerItem.pane;
      if (layerItem.isVisible) {
        layerItem.nativeLayer.addTo(this.map);
      }
    });
  }

  private removeMapLayers(layerItems: LayerItemV2[]): void {
    if (!this.map) {
      return;
    }

    layerItems.forEach((layerItem) => {
      this.removePane(layerItem.pane);

      try {
        layerItem.nativeLayer.remove();
      } catch (e) {
        console.error(e);
      }

      // Remove additional layers
      this.map.eachLayer((nativeLayer) => {
        if (this._genericMapService.hasLayerId(nativeLayer, layerItem, layerItem.layerId)) {
          try {
            nativeLayer.remove();
          } catch (e) {
            console.error(e);
          }
        }
      });
    });
  }

  private getBaseMapLayer(): LayerItem {
    return new LayerItem({
      name: 'BaseMap',
      pane: 'baseMap',
      namespace: 'baselayer',
      nativeLayer: Leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        autoZIndex: 0,
        srs: 'EPSG:4326',
        pane: this._basePane,
        maxZoom: 20,
        maxNativeZoom: 18,
      }),
      layerInfo: new LayerInfo({ elementType: undefined, layerType: LayerInfoTypeEnum.Topology }),
    });
  }

  private buildPanes(layerItems: LayerItemV2[]): void {
    asEnumerable(layerItems.map((x) => x.pane))
      .Distinct()
      .ToArray()
      .forEach((pane) => {
        this.map.createPane(pane);
        const mapPane = this.map.getPane(pane);
        const selectedLayer = layerItems.find((x) => x.pane == pane);

        if (typeof selectedLayer?.paneIndex !== 'undefined') {
          mapPane.style.zIndex = selectedLayer.paneIndex;
        }
      });
  }

  private sortPanes(sortedLayerItems: LayerItemV2[]): void {
    // Refactored: avoid removing/re-adding layers (caused temporary disappearance until zoom)
    if (!this.map || !sortedLayerItems?.length) {
      return;
    }

    this.layerItems = sortedLayerItems;
    this.removeMapLayers(sortedLayerItems);
    this.addMapLayers(sortedLayerItems);
    setTimeout(() => {
      try {
        this.map.invalidateSize();
      } catch {}
      // Fixes the issue of the polygon layers not rendering
      this.map.fire('move');
      this.map.fire('moveend');
    }, 0);
    this._layerItemsChange$.next(sortedLayerItems);
  }

  private getSortedLayerItems(layerItems: LayerItemV2[]): LayerItemV2[] {
    if (!layerItems?.length) {
      return [];
    }
    return [...layerItems].sort((a, b) => b.paneIndex - a.paneIndex);
  }

  private bindEvents(): void {
    if (!this._useGeolocatedTooltip) {
      return;
    }

    this.map.on('click', (event) => {
      const clickLatLng = event.latlng;
      const allNativeLayers = this._genericMapService.getMapNativeLayers();

      const visibleLayerItems = this.layerItems.filter((layerItem) => layerItem.isVisible);

      let found = false;
      for (let layerItem of visibleLayerItems) {
        if (found) {
          break;
        }
        const layerType = layerItem.layerDefinition.layerType;
        const groupLayer = this._genericMapService.getNativeFromLayerItem(
          allNativeLayers,
          layerItem,
        );

        groupLayer.eachLayer((innerLayer) => {
          if (found) {
            return;
          }

          if (innerLayer._containsPoint) {
            const clickPoint = this.map.latLngToContainerPoint(clickLatLng);
            if (innerLayer._containsPoint(clickPoint)) {
              this.displayTooltip(innerLayer, layerItem, clickLatLng);
              found = true;
              return;
            }
          }

          if (
            this._genericMapTooltipService.isClickedLayer(
              layerType,
              clickLatLng,
              innerLayer,
              this.map,
            )
          ) {
            this.displayTooltip(innerLayer, layerItem, clickLatLng);
            found = true;
            return;
          }
        });
      }
      if (!found) {
        this._log.log('[bindEvents] No layer matched for click');
      }
    });
  }

  onDateRangeChanged(dateRange: DateRange) {
    this._genericMapDataService.setCurrentDateRange(dateRange);
  }
  onStepChanged(step: Date): void {
    if (!this.layerItems.length || this.selectedStep?.toString() == step?.toString()) {
      return;
    }

    this.notifyChildMapStateChanged(true);

    const callId = globalUtilsHelper.generateGuid();

    this._log.log(`Step Change: Start load step: ${step}`, { callId });

    this.selectedStep = step;
    this._genericMapService.setSelectedStep(step);
    this._genericMapDataService.setCurrentTimeStep(step);
    this.timeStepChange.emit(step);

    this._setResultsToLayersSubs?.unsubscribe();

    this.allLayersLoaded$().subscribe(() => {
      const nativeLayers = this._genericMapService.getMapNativeLayers();

      this._log.log(`Step Change: layers stable`, { callId });

      this._setResultsToLayersSubs = this._genericMapService
        .setResultsToLayers(this.layerItems, this.selectedStep, nativeLayers)
        .subscribe({
          next: (layerItems) => {
            this.layerItems = layerItems;
            // layerItems.forEach((layerItem) => {
            //   this._genericMapService.refreshStyles(layerItem, this.layerItems);
            // });
            this.layerWithResultsChange.emit(layerItems);
            this._layerItemsChange$.next(layerItems);
            this._log.log(`Step Change: Loaded results for step ${step}`, { callId });
            this.bindTooltip();
            this.updateTooltipContent();            
            this.notifyChildMapStateChanged(false);
          },
          error: (error) => {
            this.handleError(error);
            this.notifyChildMapStateChanged(false);
          },
        });
    });
  }

  //Call this when the style for a layer changes
  onStyleChanged(layerItem: LayerItemV2, selectedStyle: MapGenericStyle, isUpdate?: boolean): void {
    layerItem.layerStyle = selectedStyle;
    this._genericMapService.refreshStyles(layerItem, this.layerItems);
  }

  //Call this when layer visibility changes
  onLayerVisibilityChanged(event: LayerVisibilityChangeEvent): void {
    let selectedLayerItem: LayerItemV2;

    if (event.layerItem.layerDefinition?.isThematic) {
      selectedLayerItem = this.layerItems.find(
        (layerItem) => layerItem.layerStyle.id === event.layerItem.layerStyle.id,
      );
    } else {
      selectedLayerItem = this.layerItems.find(
        (layerItem) => layerItem.layerId === event.layerItem.layerId,
      );
    }

    this._genericMapService.setLayerVisibility(selectedLayerItem, event.visible).subscribe({
      next: (changedLayerItem) => {
        const changedLayerItems = [changedLayerItem];
        if (event.visible) {
          if (!this.map.getPane(changedLayerItem.pane)) {
            this.map.createPane(changedLayerItem.pane);
          }
          changedLayerItem.nativeLayer.pane = changedLayerItem.pane;
          if (!this.map.hasLayer(changedLayerItem.nativeLayer)) {
            try {
              changedLayerItem.nativeLayer.addTo(this.map);
            } catch {}
          }
        } else {
          try {
            changedLayerItem.nativeLayer.remove();
          } catch {}
        }
        this.updateVisibleLayersOrder();
        this.layersVisibilityChange.emit(changedLayerItems);
        this._layerItemsChange$.next(changedLayerItems);
        this._genericMapService.persistLayersOrder(this.layerItems);

        this.sendLayerItemsVisibles(this.layerItems);
        this.refreshMap();
        this.bindTooltip();
      },
      error: this.handleError,
    });
  }

  private updateVisibleLayersOrder(): void {
    if (!this.map || !this.layerItems?.length) {
      return;
    }
    let z = 100;
    for (let i = this.layerItems.length - 1; i >= 0; i--) {
      const li = this.layerItems[i];
      if (li.isVisible) {
        li.paneIndex = z++;
        let pane = this.map.getPane(li.pane);
        if (!pane) {
          pane = this.map.createPane(li.pane);
        }
        if (pane) {
          pane.style.zIndex = String(li.paneIndex);
        }
      }
    }
  }

  private refreshMap(): void {
    if (!this.map) {
      return;
    }

    try {
      this.layerItems
        ?.filter((li) => li.isVisible)
        .forEach((li) => {
          if (li.nativeLayer) {
            if (!this.map.hasLayer(li.nativeLayer)) {
              try {
                li.nativeLayer.addTo(this.map);
              } catch (error) {
                console.error(error);
              }
            }
            const nl: any = li.nativeLayer as any;
            if (nl && typeof nl.bringToFront === 'function') {
              try {
                nl.bringToFront();
              } catch (error) {
                console.error(error);
              }
            }
          }
        });

      this.layerItems?.forEach((li) => {
        if (typeof li.paneIndex === 'number') {
          const pane = this.map.getPane(li.pane);
          if (pane) {
            pane.style.zIndex = String(li.paneIndex);
          }
        }
      });
    } catch (error) {
      console.error(error);
    }

    requestAnimationFrame(() => {
      try {
        this.map.invalidateSize();
      } catch (error) {
        console.error(error);
      }
    });
  }

  onExternalFilterChanged(filters: Partial<IMapFiltrableProperties>): void {
    if (!this.layerItems) {
      return;
    }

    this._genericMapService.setExtraFilters(this.layerItems, filters).subscribe({
      next: (newLayerItems) => {
        this.removeMapLayers(this.layerItems);
        this.layerItems = newLayerItems;
        this.addMapLayers(this.layerItems);

        setTimeout(() => {
          this._genericMapService.applyStylesToBaseLayers(this.layerItems, this.map);
        });
      },
      error: this.handleError,
    });
  }

  onTabSelectionChange(isExpanded: boolean): void {
    //this.isSidePanelCollapsed = !isExpanded;
  }

  onLayerItemStyleChange(event: LayerItemStyleChangeData): void {
    this.onStyleChanged(event.layer, event.selectedStyle, event.isUpdate);
  }

  onLayerItemsOrderChanged(sortedLayerItems: LayerItemV2[]): void {
    this.sortPanes(sortedLayerItems);
    this._genericMapService.persistLayersOrder(sortedLayerItems);
  }

  private addSidePanel(): void {
    if (this._sidePanel && Leaflet.control?.sidebar && this.map && !this._sidePanelSetted) {
      const container = this._sidePanel.getNativeElement();
      Leaflet.control.sidebar({ container }).addTo(this.map);
      this._sidePanelSetted = true;
    }
  }

  private addTimeBar(): void {
    if (this._timeBar && this.map && !this._timeBarSetted) {
      const container = this._timeBar.getNativeElement();
      Leaflet.control.waterTimeBarControl({ container }).addTo(this.map);
      this._timeBarSetted = true;
    }
  }

  private addTopBar(): void {
    if (this._topBar && Leaflet.control?.sidebar && this.map && !this._topBarSetted) {
      const container = this._topBar.getNativeElement();
      Leaflet.control.sidebar({ container }).addTo(this.map);
      this._topBarSetted = true;
    }
  }

  private notifyChildMapStateChanged(isLoading: boolean): void {
    if (this._timeBar && this.map && this._timeBarSetted) {
      this._timeBar.onMapLoadingStateChanged(isLoading);
    }
  }

  private get firstStep() {
    return this._mapSettings.dateRange.startDate ?? this._mapSettings.dateRange.steps[0];
  }

  private buildThematicLayerItem(thematic: LayerThematicStylesDto): LayerItemV2 | undefined {
    const baseLayerItem = this.layerItems.find(
      (layerItem) => layerItem.pane === thematic.objectType,
    );

    return this._thematicMapService.buildThematicLayerItem(
      baseLayerItem,
      thematic,
      this.layerItems,
    );
  }

  private handleError = (error) => {
    // this._dialogService.showErrorMessage(error);
    console.error(error);
  };
  /**
   * Removes a pane and all its layers from the map.
   * @param paneName The name of the pane to remove.
   */
  private removePane(paneName: string): void {
    if (!this.map) {
      return;
    }

    // 1. Remove all layers in this pane
    this.map.eachLayer((layer: any) => {
      if (layer.options && layer.options.pane === paneName) {
        this.map.removeLayer(layer);
      }
    });

    // 2. Remove the pane's DOM element
    const pane = this.map.getPane(paneName);
    if (pane && pane.parentNode) {
      pane.parentNode.removeChild(pane);
    }

    // 3. Remove the pane reference from Leaflet's internal map
    if (this.map._panes && this.map._panes[paneName]) {
      delete this.map._panes[paneName];
    }
  }

  /**
   * Enables click listeners on all vector layers (Point, Polygon, LineString) to show a standalone tooltip at the click position.
   * The tooltip is not bound to the layer and the clicked layer is accessible in the event handler.
   */
  private bindTooltip(): void {
    if (!this.map || this._useGeolocatedTooltip) {
      return;
    }

    const callId = globalUtilsHelper.generateGuid();

    this._log.log(`Bind tooltips: Start`, { callId });

    this.allLayersLoaded$().subscribe(() => {
      this._log.log(`Bind tooltips: All layers loaded`, { callId });

      let bindingCount = 0;
      this.map?.eachLayer((nativeLayer: NativeLayer) => {
        const layerItem = this.getLayerItemFromNative(nativeLayer);

        if (layerItem) {
          nativeLayer.eachLayer((layer) => {
            // Remove previous click handler if it exists
            if (layer._standaloneTooltipClickHandler) {
              layer.off('click', layer._standaloneTooltipClickHandler);
            }
            // Define and store the new click handler
            const clickHandler = (event) => {
              const coordinates = event.latlng;
              this.displayTooltip(layer, layerItem, coordinates);
            };
            layer._standaloneTooltipClickHandler = clickHandler;
            layer.on('click', clickHandler);
            bindingCount++;
          });
        }
      });

      this._log.log(`Bind tooltips: Bound ${bindingCount} layers`, { callId });
    });
  }

  private allLayersLoaded$(): Observable<NativeLayer[]> {
    return this._genericMapService.allLayersLoaded$(this.layerItems);
  }

  private displayTooltip(layer: NativeLayer, layerItem: LayerItemV2, coordinates): void {
    if (layerItem) {
      const layerItemService = this._genericMapService.getLayerItemService(layerItem);
      const tooltipParams: MapTooltipParams = {
        nativeLayer: layer,
        allLayerItems: this.layerItems,
        layerItem,
        layerItemsChange$: this._layerItemsChange$,
        layerItemService,
      };

      this._standaloneTooltipParams = tooltipParams;
      this._standaloneTooltipLatLng = coordinates;
      this.displayToolTip();
    }
  }

  private displayToolTip(){
    this.ensureStandaloneTooltipPane();
    
    if (this._standaloneTooltip){
      this.map.removeLayer(this._standaloneTooltip);
      this._standaloneTooltip = null;
    }
    
    this._genericMapService.getTooltip(this._standaloneTooltipParams).subscribe((tooltipContent) => {
      this._standaloneTooltip = Leaflet.tooltip({
        permanent: false,
        direction: 'top',
        className: 'standalone-tooltip',
        interactive: true,
        pane: 'standaloneTooltipPane',
      })
        .setLatLng(this._standaloneTooltipLatLng)
        .setContent(tooltipContent)
        .addTo(this.map);
    });
  }

  private updateTooltipContent(){
    if (this._standaloneTooltip?.isOpen()){
      this.displayToolTip();
    }
  }

  private ensureStandaloneTooltipPane(): void {
    if (!this.map) return;
    const existing = this.map.getPane('standaloneTooltipPane');
    if (!existing) {
      const pane = this.map.createPane('standaloneTooltipPane');
      pane.style.zIndex = '9999';
      pane.style.pointerEvents = 'none';
    }
  }

  private sendLayerItemsVisibles(layerItems: LayerItemV2[]) {
    const visibleLayers = layerItems.filter((li) => li.isVisible);
    this._layersService.setAllLayersVisibles(visibleLayers);
  }

  private handleThematicEdited(thematic: LayerThematicStylesDto): void {
    if (thematic) {
      this.layerItems.forEach((layerItem) => {
        if (layerItem.layerStyle.id === thematic.id) {
          layerItem.layerStyle = {
            ...layerItem.layerStyle,
            ...thematic,
          } as any;

          this._genericMapService.refreshStyles(layerItem, this.layerItems);
        }
      });
    }
  }

  private handleThematicCreated(thematic: LayerThematicStylesDto): void {
    if (thematic) {
      const layerItem = this.buildThematicLayerItem(thematic);

      if (layerItem) {
        const existingId = this.layerItems?.findIndex(
          (li) => li.layerStyle?.id && li.layerStyle.id === layerItem.layerStyle?.id,
        );

        this._genericMapService.buildLayerItems([layerItem]).subscribe({
          next: (layerItems) => {
            this._log.log('Thematics: new created', layerItems);
            const processedLayerItem = layerItems[0];

            if (existingId !== undefined && existingId > -1) {
              const duplicated = [this.layerItems[existingId]];
              this.layerItems.splice(existingId, 1);
              this.removeMapLayers(duplicated);
              this._ctMapPersistencyService.persistThematicDeleted(duplicated[0]);
              this.layerItems.splice(existingId, 0, processedLayerItem);
            }

            this.layerItems = this.layerItems.concat(processedLayerItem);
            this.layerItems = this.getSortedLayerItems(this.layerItems);
            this._genericMapService.refreshStyles(processedLayerItem, this.layerItems);
            this._ctMapPersistencyService.persistThematicCreated(processedLayerItem);
            this.onLayerItemsOrderChanged(this.layerItems);
            this.sendLayerItemsVisibles([processedLayerItem]);

            this._layerItemsChange$.next(this.layerItems);
            this.bindTooltip();
          },
        });
      }
    }
  }

  private handleThematicToDelete(layerItem: LayerItemV2) {
    if (layerItem) {
      const existingLayerItem = this.layerItems.find((li) => li === layerItem);
      if (existingLayerItem) {
        existingLayerItem.isVisible = false;
        this.removeMapLayers([existingLayerItem]);
        this.sendLayerItemsVisibles([existingLayerItem]);
        this.layerItems = this.layerItems.filter((li) => li !== existingLayerItem);
        this._ctMapPersistencyService.persistThematicDeleted(existingLayerItem);
        this._layerItemsChange$.next(this.layerItems);
      }
    }
  }

  private getLayerItemFromNative(nativeLayer: NativeLayer): LayerItemV2 {
    const layerItem = this.layerItems.find((layerItem) => layerItem.pane === nativeLayer.pane);
    return layerItem;
  }

  private isMapEmptyElementLoaded = (element: ElementRef<any>) =>
    element?.nativeElement && !element.nativeElement.classList.contains('leaflet-container');

  private eachMapLayer(callback: (nativeLayer: NativeLayer, layerItem: LayerItemV2) => void): void {
    this.map.eachLayer((nativeLayer: NativeLayer) => {
      const layerItem = this.getLayerItemFromNative(nativeLayer);

      if (layerItem && nativeLayer?.eachLayer) {
        nativeLayer.eachLayer((layer) => {
          callback(layer, layerItem);
        });
      }
    });
  }
}
